import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './LandingPage.module.css';
import api from '../../services/api.js'; 
import Swal from 'sweetalert2';
import Loader from '../../components/Loader';
import showToast from '../../components/ShowToast';

// Icons including Eye/EyeSlash
import { FaHome, FaBriefcase, FaQuoteLeft, FaBuilding, FaEnvelope, FaCheckCircle, FaUser, FaLock, FaMapPin, FaPhone, FaEye, FaEyeSlash } from 'react-icons/fa';
import kitchenImage from '../../assets/images/resi02.jpeg';
import imageLogo from "../../assets/images/BSPLogoTranSmall.png";


// --- 1. Role Selection Page Component (Unchanged) ---
export const RoleSelectPage = () => {
    return (
        <div className={styles.roleSelectContainer}>
          <a href="/" className={styles.authLogo}>
            <img src={imageLogo} alt="Build Start Logo" style={{ height: '35px', marginRight: '10px' }} />
        
          </a>
            <h1 className={styles.authTitle}>Join Build Start</h1>
            <p className={styles.authSubtitle}>First, tell us who you are. This will help us tailor your experience.</p>
            <div className={styles.roleCardsContainer}>
                <a href="/register/homeowner" className={styles.roleCard}>
                    <FaHome className={styles.roleIcon} />
                    <h2>I'm a Homeowner</h2>
                    <p>Hiring professionals for a home project.</p>
                </a>
                <a href="/register/business" className={styles.roleCard}>
                    <FaBriefcase className={styles.roleIcon} />
                    <h2>I'm a Consultant, Trade or Supplier</h2>
                    <p>Looking for clients and to grow my business.</p>
                </a>
                <a href="/register/developer" className={styles.roleCard}>
                    <FaBuilding className={styles.roleIcon} />
                    <h2>I'm a Renovator / Developer</h2>
                    <p>Sourcing subcontractors for large-scale projects.</p>
                </a>
            </div>
             <p className={styles.authRedirect}>
                Already have an account? <a href="/login" className={styles.link}>Log In</a>
            </p>
        </div>
    );
};


// --- 2. Registration Page (Unified Optimized Layout) ---
export const RegistrationPage = ({ role }) => {
  const isBusiness = role === 'business';
  const isDeveloper = role === 'developer';

  const navigate = useNavigate();
  const location = useLocation(); 

  // URL Params (for Claiming Logic)
  const params = new URLSearchParams(location.search);
  const claimId = params.get('claim_id');

  // --- Capture Search Params ---
  const urlTrade = params.get('trade');
  const urlPostcode = params.get('postcode');

  // --- 2. CAPTCHA STATE ---
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null); // Reference to the DIV where captcha goes
  const widgetId = useRef(null); // To keep track of the widget instance

  // State
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  
  // UI Toggles
  const [showPassword, setShowPassword] = useState(false);
  const [claimingBusiness, setClaimingBusiness] = useState(null); 

    // --- NEW: Dynamic Header State ---
  const [dynamicHeader, setDynamicHeader] = useState('');

  // Unified Form Data 
  // - Removed firstname/lastname -> merged to fullname
  // - Removed confirmpwd
  const initialFormData = {
    businessname: '', 
    developerbusinessname: '',
    email: '',
    pwd: '',
    phone: '',
    fullname: '', 
    postcode: '',
    suburb: ''
  };

  const [formData, setFormData] = useState(initialFormData);



// --- PRE-FILL & TITLE LOGIC ---
  useEffect(() => {
    // 1. If we have a postcode from the landing page, set it
    if (urlPostcode && !formData.postcode) {
        setFormData(prev => ({ ...prev, postcode: urlPostcode }));
    }

    // 2. If we have a trade, update the header and save for Onboarding
    if (urlTrade && isBusiness) {
        // "Create Business Account for Plumber Opportunities"
        setDynamicHeader(`Create Business Account for ${urlTrade} Opportunities`);
        
        // Save to local storage so you can retrieve it in the Onboarding Step later
        localStorage.setItem('onboarding_prefill_trade', urlTrade);
    }
  }, [urlTrade, urlPostcode, isBusiness]);



// --- LOAD TURNSTILE ---
  useEffect(() => {
    // 1. Check if script is already present
    const scriptId = 'cloudflare-turnstile-script';
    let script = document.getElementById(scriptId);

    if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        document.body.appendChild(script);
    }

    // 2. Wait for the global window.turnstile object to be ready
    const interval = setInterval(() => {
        if (window.turnstile && turnstileRef.current) {
            clearInterval(interval);
            
            // Clean up previous instance if strict mode re-runs this
            if (widgetId.current) window.turnstile.remove(widgetId.current);

            // 3. Render the widget
            try {
                widgetId.current = window.turnstile.render(turnstileRef.current, {
                    sitekey: '0x4AAAAAACLyRZf_NiiPXSqz', // <--- PUT YOUR SITE KEY HERE
                    callback: (token) => setTurnstileToken(token),
                    'expired-callback': () => setTurnstileToken(''),
                    'error-callback': () => setTurnstileToken(''),
                });
            } catch (e) {
                console.error("Turnstile render error:", e);
            }
        }
    }, 100);

    return () => clearInterval(interval);
  }, []);



  // --- CLAIM LOGIC (Only runs if Business + Claim ID) ---
  useEffect(() => {
    if (claimId && isBusiness) {
        api.get(`/api/Leads/info/${claimId}`)
           .then(response => {
               const biz = response.data || response; 
               setClaimingBusiness(biz);
               
               // Only pre-fill business data, keep user data empty
               setFormData(prev => ({
                   ...prev,
                   businessname: biz.businessName || biz.businessname,
                   postcode: biz.postcode || '',
                   suburb: biz.suburb || ''
               }));
           })
           .catch(err => console.error("Failed to load claim data", err));
    }
  }, [claimId, isBusiness]);

  // --- VALIDATION ---
  const validate = () => {
    const newErrors = {};
    
    // Business Name Validation
    if (isBusiness && !claimingBusiness && !formData.businessname) newErrors.businessname = 'Business name is required';
    if (isDeveloper && !formData.developerbusinessname) newErrors.developerbusinessname = 'Business name is required';

    // Full Name Validation
    if (!formData.fullname || formData.fullname.trim().indexOf(' ') === -1) {
        newErrors.fullname = 'Please enter your first and last name';
    }

    // Email Validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }

    // Mobile Validation
    if (!formData.phone) {
        newErrors.phone = 'Mobile number is required';
    }

    // Postcode Validation (Required for everyone, unless claiming business)
    if (!claimingBusiness && !formData.postcode) {
        newErrors.postcode = 'Postcode is required';
    }

    // Password Validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;
    if (!formData.pwd) {
      newErrors.pwd = 'Password is required';
    } else if (!passwordRegex.test(formData.pwd)) {
      newErrors.pwd = 'Strong password required (8+ chars, Uppercase, Number)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'postcode' && !/^\d{0,4}$/.test(value)) return;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

   // --- CHECK TOKEN ---
    if (!turnstileToken) {
        showToast('error', 'Please complete the security check.');
        return;
    }


    if (!validate()) {
      showToast('error', 'Please fix the errors.');
      return;
    }
    setLoading(true);
    setServerError('');

    const endpoint = isBusiness ? '/api/RegisterBusinessUser' : '/api/RegisterHomeUser';
    
    // LOGIC: Split "Full Name" back into First/Last for the API
    const nameParts = formData.fullname.trim().split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || '.'; // Handle single names gracefully

    const payload = {
      email: formData.email,
      pwd: formData.pwd,
      confirmPwd: formData.pwd, // Send same password as confirm
      firstName: firstName,
      lastName: lastName,
      phone: formData.phone,
      
      // Location Logic: Use Claim data if claiming, otherwise Form data
      postcode: claimingBusiness ? claimingBusiness.postcode : formData.postcode,
      suburb: claimingBusiness ? claimingBusiness.suburb : formData.suburb,
      
      // Business Name Logic
      ...(isBusiness && { 
          businessname: claimingBusiness ? (claimingBusiness.businessName || claimingBusiness.businessname) : formData.businessname 
      }),
      ...(isDeveloper && { developerBusinessName: formData.developerbusinessname }),

      claimBizId: claimId ? parseInt(claimId) : null,

      // --- 4. SEND TOKEN TO BACKEND ---
      captchaToken: turnstileToken 
    };

    try {
      
      const response = await api.post(endpoint, payload);

      Swal.fire({
        title: "Success!",
        text: "Account created. Please check your email to activate.",
        icon: "success",
        confirmButtonText: "Log In"
      }).then((result) => {
        if (result.isConfirmed) navigate('/login'); 
      });
    } catch (error) {
      let msg = 'An unknown error occurred.';
      if (error.response?.data?.message) msg = error.response.data.message;
      else if (error.response?.data?.errors) msg = Object.values(error.response.data.errors).flat().join(' ');
      
        setServerError(msg);
        showToast('error', 'Registration failed.');

     // --- RESET CAPTCHA ---
      if (window.turnstile && widgetId.current) {
          window.turnstile.reset(widgetId.current);
          setTurnstileToken('');
      }


    } finally {
      setLoading(false);
    }
  };

  const getTestimonial = () => {
    if (isBusiness) return { quote: "Joining Build Start was the best decision. 40% more leads.", author: "Mark C.", authorDetail: "Cornerstone Plumbing" };
    if (isDeveloper) return { quote: "Our go-to for sourcing reliable trade crews.", author: "David Chen", authorDetail: "Apex Developments" };
    return { quote: "I connected with a 5-star professional in minutes.", author: "Sarah L.", authorDetail: "Homeowner" };
  };
  const testimonial = getTestimonial();

  const getTitle = () => {
      if (claimingBusiness) return `Claiming: ${claimingBusiness.businessName || claimingBusiness.businessname}`;
      if (isBusiness) return 'Create Business Account';
      if (isDeveloper) return 'Create Developer Account';
      return 'Create Homeowner Account';
  }

  return (
    <div className={styles.authContainer}>
      {loading && <Loader />}
      
      {/* Visual Sidebar */}
      <div className={`${styles.authVisual} ${styles.testimonialVisual}`}>
        <div className={styles.testimonialContainer}>
            <FaQuoteLeft className={styles.testimonialQuoteIcon} />
            <p className={styles.testimonialText}>"{testimonial.quote}"</p>
            <div className={styles.testimonialAuthor}>
                <div><strong>{testimonial.author}</strong><span>{testimonial.authorDetail}</span></div>
            </div>
        </div>
      </div>
    
      <div className={styles.authFormContainer}>
        <div className={styles.authForm}>
          
          <a href="/" className={styles.authLogo}>
             <img src={imageLogo} alt="Logo" style={{ height: '35px', marginRight: '10px' }} />
             
          </a>

          <h1 className={styles.authTitle} style={claimingBusiness ? {fontSize:'1.8rem'} : {}}>{getTitle()}</h1>

          {claimingBusiness ? (
             <div style={{textAlign:'center', marginBottom: '30px'}}>
                 <p style={{color: '#64748b', fontSize: '1.1rem', marginTop: '-5px'}}>
                    <FaMapPin style={{marginRight:'5px', color:'#2c3e50'}}/> 
                    {claimingBusiness.suburb}, {claimingBusiness.postcode}
                 </p>
                 <div style={{background: '#f0f9ff', padding: '10px', borderRadius: '8px', border: '1px solid #bae6fd', fontSize: '0.9rem', color: '#0369a1'}}>
                    <FaCheckCircle style={{marginRight: '6px'}}/> 
                    Join Today and get your FREE One Month Leads Subscription
                 </div>
             </div>
          ) : (
             <p className={styles.authSubtitle}>
                  <div style={{background: '#f0f9ff', padding: '10px', borderRadius: '8px', border: '1px solid #bae6fd', fontSize: '0.9rem', color: '#0369a1'}}>
                    <FaCheckCircle style={{marginRight: '6px'}}/> 
                    Join Today and get your FREE One Month Leads Subscription
                 </div>

              </p>
          )}

          <form onSubmit={handleSubmit}>
            
            {/* 1. BUSINESS NAME FIELD (Conditional) */}
            {isBusiness && !claimingBusiness && (
                <div className={styles.formGroup}>
                    <label>Business Name</label>
                    <div className={styles.formBox}>
                        <input name="businessname" value={formData.businessname} onChange={handleChange} className={styles.formInput} />
                    </div>
                    {errors.businessname && <p className={styles.errorText}>{errors.businessname}</p>}
                </div>
            )}
            {isDeveloper && (
                <div className={styles.formGroup}>
                     <label>Business Name</label>
                     <div className={styles.formBox}>
                        <input name="developerbusinessname" value={formData.developerbusinessname} onChange={handleChange} className={styles.formInput} />
                     </div>
                     {errors.developerbusinessname && <p className={styles.errorText}>{errors.developerbusinessname}</p>}
                </div>
            )}

            {/* 2. FULL NAME (Merged) */}
            <div className={styles.formGroup}>
                <label>Your Full Name</label>
                <div className={styles.formBox} style={{position:'relative'}}>
                    <input 
                        type="text" 
                        name="fullname" 
                        value={formData.fullname} 
                        onChange={handleChange} 
                        className={styles.formInput} 
                        placeholder="e.g. John Smith"
                        style={{paddingLeft: '40px'}}
                    />
                    <FaUser style={{position: 'absolute', left: '12px', top: '13px', color: '#aaa'}} />
                </div>
                {errors.fullname && <p className={styles.errorText}>{errors.fullname}</p>}
            </div>

            {/* 3. EMAIL */}
            <div className={styles.formGroup}>
                <label>Email Address</label>
                <div className={styles.formBox} style={{position:'relative'}}>
                    <input 
                        type="email" 
                        name="email" 
                        value={formData.email} 
                        onChange={handleChange} 
                        className={styles.formInput}
                        style={{paddingLeft: '40px'}}
                    />
                    <FaEnvelope style={{position: 'absolute', left: '12px', top: '13px', color: '#aaa'}} />
                </div>
                {errors.email && <p className={styles.errorText}>{errors.email}</p>}
            </div>

            {/* 4. MOBILE */}
            <div className={styles.formGroup}>
                <label>Mobile Number</label>
                <div className={styles.formBox} style={{position:'relative'}}>
                    <input 
                        type="tel" 
                        name="phone" 
                        value={formData.phone} 
                        onChange={handleChange} 
                        className={styles.formInput}
                        style={{paddingLeft: '40px'}}
                    />
                    <FaPhone style={{position: 'absolute', left: '12px', top: '13px', color: '#aaa'}} />
                </div>
                {errors.phone && <p className={styles.errorText}>{errors.phone}</p>}
            </div>

            {/* 5. POSTCODE (Conditional) */}
            {!claimingBusiness && (
                <div className={styles.formGroup}>
                    <label>Postcode</label>
                    <div className={styles.formBox} style={{position:'relative'}}>
                         <input name="postcode" value={formData.postcode} onChange={handleChange} className={styles.formInput} style={{paddingLeft: '40px'}}/>
                         <FaMapPin style={{position: 'absolute', left: '12px', top: '13px', color: '#aaa'}} />
                    </div>
                    {errors.postcode && <p className={styles.errorText}>{errors.postcode}</p>}
                </div>
            )}

            {/* 6. PASSWORD (With Toggle) */}
            <div className={styles.formGroup}>
                <label>Password</label>
                <div className={styles.formBox} style={{position:'relative'}}>
                    <input 
                        type={showPassword ? "text" : "password"}
                        name="pwd" 
                        value={formData.pwd} 
                        onChange={handleChange} 
                        className={styles.formInput}
                        style={{paddingLeft: '40px', paddingRight: '40px'}}
                    />
                    <FaLock style={{position: 'absolute', left: '12px', top: '13px', color: '#aaa'}} />
                    
                    {/* Toggle Icon */}
                    <div 
                        onClick={() => setShowPassword(!showPassword)}
                        style={{position: 'absolute', right: '12px', top: '13px', cursor: 'pointer', color: '#666'}}
                    >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </div>
                </div>
                {errors.pwd && <p className={styles.errorText}>{errors.pwd}</p>}
            </div>


            {/* ---  CAPTCHA CONTAINER --- */}
            <div className={styles.formGroup} style={{marginTop: '20px', display:'flex', justifyContent:'center'}}>
                {/* The widget will appear inside this DIV */}
                <div ref={turnstileRef}></div> 
            </div>

        

            {serverError && (
              <div className={styles.serverErrorContainer}>
                <p className={styles.errorText}>{serverError}</p>
              </div>
            )}

            
            <div className={styles.terms} style={{marginTop: '10px'}}>
                <input type="checkbox" id="terms" name="terms" required />
                <label htmlFor="terms">I agree to the <a href="/terms" className={styles.link}>Terms</a> and <a href="/privacy" className={styles.link}>Privacy Policy</a>.</label>
            </div>
            
            <button type="submit" className={styles.btnPrimaryAuth} style={{marginTop: '20px'}}>
                {claimingBusiness ? 'Claim & Start Free Trial' : 'Create Account & Start Free Trial'}
            </button>
          </form>
          
          <p className={styles.authRedirect}>
            Already have an account? <a href="/login" className={styles.link}>Log In</a>
          </p>
        </div>
      </div>
    </div>
  );
};


// --- 3. Reset Password Page (Restored) ---
export const ResetPasswordPage = () => {
    const location = useLocation();
    
    // State to determine which view to show
    const [token, setToken] = useState(null);
    const [emailFromQuery, setEmailFromQuery] = useState(null);

    // This effect checks the URL for a token on page load
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tokenFromQuery = queryParams.get('token');
        const email = queryParams.get('email');
        if (tokenFromQuery && email) {
            setToken(tokenFromQuery);
            setEmailFromQuery(email);
        }
    }, [location]);

    // VIEW 1: REQUEST LINK
    const RequestFormView = () => {
        const [email, setEmail] = useState('');
        const [submitted, setSubmitted] = useState(false);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        const handleSubmit = async (e) => {
            e.preventDefault();
            setError('');
            setLoading(true);
            try {
                await api.post(`/api/auth/request-password-reset`, { email });
                setLoading(false);
                setSubmitted(true); 
            } catch (err) {
                setLoading(false);
                setError(err.response?.data?.message || 'An error occurred. Please try again.');
            }
        };

        if (submitted) {
            return (
                <div className={styles.authForm}>
                    <div className={styles.confirmationContainer}>
                        <FaEnvelope className={styles.confirmationIcon} />
                        <h1 className={styles.authTitle}>Check your email</h1>
                        <p className={styles.authSubtitle}>
                            We've sent a password reset link to <strong>{email}</strong>.
                        </p>
                        <Link to="/login" className={`${styles.btnPrimaryAuth} ${styles.linkButton}`}>Back to Login</Link>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.authForm}>
              <a href="/" className={styles.authLogo}>
                <img src={imageLogo} alt="Logo" style={{ height: '35px', marginRight: '10px' }} />
                
              </a>
                <h1 className={styles.authTitle}>Forgot Password?</h1>
                <p className={styles.authSubtitle}>Enter your email to receive a reset link.</p>
                <form onSubmit={handleSubmit}>
                    <div className={styles.formGroup}>
                        <label>Email Address</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={styles.formInput} />
                         {error && <p className={styles.errorText}>{error}</p>}
                    </div>
                    <button type="submit" className={styles.btnPrimaryAuth} disabled={loading}>
                        {loading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                </form>
                <p className={styles.authRedirect}><Link to="/login" className={styles.link}>← Back to Login</Link></p>
            </div>
        );
    };

    // VIEW 2: SET NEW PASSWORD
    const ResetFormView = () => {
        const [password, setPassword] = useState('');
        const [error, setError] = useState('');
        const [loading, setLoading] = useState(false);
        const [success, setSuccess] = useState(false);
        const [showPassword, setShowPassword] = useState(false); // Added toggle here too

        const handleSubmit = async (e) => {
            e.preventDefault();
            setError('');
            
            // Regex check
            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
            if (!passwordRegex.test(password)) {
                 setError('Password must be 8+ chars with Uppercase, Number, and Special Char.');
                 return;
            }
            
            setLoading(true);
            try {
                await api.post(`/api/auth/reset-password`, {
                    email: emailFromQuery,
                    token: token,
                    newPassword: password,
                });
                setLoading(false);
                setSuccess(true);
            } catch (err) {
                setLoading(false);
                setError(err.response?.data?.message || 'Invalid or expired token.');
            }
        };

        if (success) {
             return (
                <div className={styles.authForm}>
                    <div className={styles.confirmationContainer}>
                        <FaCheckCircle className={styles.confirmationIcon} />
                        <h1 className={styles.authTitle}>Password Reset!</h1>
                        <p className={styles.authSubtitle}>Your password has been changed successfully.</p>
                        <Link to="/login" className={`${styles.btnPrimaryAuth} ${styles.linkButton}`}>Proceed to Login</Link>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.authForm}>
                <Link to="/" className={styles.authLogo}>Build Start</Link>
                <h1 className={styles.authTitle}>Create New Password</h1>
                <p className={styles.authSubtitle}>Your new password must be different from previous ones.</p>
                <form onSubmit={handleSubmit}>
                    <div className={styles.formGroup}>
                        <label>New Password</label>
                         <div className={styles.formBox} style={{position:'relative'}}>
                            <input 
                                type={showPassword ? "text" : "password"}
                                value={password} 
                                onChange={(e) => setPassword(e.target.value)} 
                                required 
                                className={styles.formInput} 
                                style={{paddingRight: '40px'}}
                            />
                            <div 
                                onClick={() => setShowPassword(!showPassword)}
                                style={{position: 'absolute', right: '12px', top: '13px', cursor: 'pointer', color: '#666'}}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </div>
                        </div>
                    </div>
                    {error && <p className={styles.errorText}>{error}</p>}
                    <button type="submit" className={styles.btnPrimaryAuth} disabled={loading}>
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                </form>
            </div>
        );
    };

    return (
        <div className={styles.authContainer}>
            <div className={styles.authVisual}>
                <img src={kitchenImage} alt="Calm and modern kitchen" />
            </div>
            <div className={styles.authFormContainer}>
                {token ? <ResetFormView /> : <RequestFormView />}
            </div>
        </div>
    );
};